import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hasFeature } from '@precert/has-feature';
import { FormSchema } from '@precert/validation';
import React from 'react';
import { Row, Col, FormGroup } from 'reactstrap';

import { useConfig } from '../../../../_components/config';
import InputField from '../../../../_components/input-field';
import AsyncMultiSelectField from '../../../../_components/select-components/async-multi-select';
import AsyncSelectField from '../../../../_components/select-components/async-select';
import SelectField from '../../../../_components/select-components/select-field';
import type { WizardStepProps } from '../wizard';

import { valuesCodeArray } from './common';
import useLookups from './lookups';

export interface StepNewRequestProps {
  index: number;
}

export interface StepNewRequestFormData {
  procedureCode: string;
  requestType: 'outpatient' | 'inpatient';
  episodicCare: boolean;
  veteranReferralId: string;
  veteranIcn: string;
}

export const StepNewRequestSchema = FormSchema().pick([
  'veteranReferralId',
  'veteranIcn',
  'procedureCode',
  'requestType',
  'episodicCare',
  'procedureCodesList',
]);

export const PrecertCodeField = ({
  showLinkAboveInput = false,
  customSubLabel,
  hideFooter = false,
}: {
  showLinkAboveInput?: boolean;
  customSubLabel?: React.ReactElement | string;
  hideFooter?: boolean;
} = {}) => {
  const { cptLookup } = useLookups();
  const { features } = useConfig();

  const cptLinkBold = (
    <a
      target="_blank"
      rel="noreferrer"
      href="https://www.va.gov/COMMUNITYCARE/providers/prct_requirements.asp"
      className="fw-bold"
    >
      View Precertification Storefront to Download List of Precertification
      Required Codes
    </a>
  );

  const cptLinkSmall = (
    <>
      Download precertification code list at{' '}
      <a
        target="_blank"
        rel="noreferrer"
        href="https://www.va.gov/COMMUNITYCARE/providers/prct_requirements.asp"
      >
        Storefront
      </a>
      .
    </>
  );

  const informationText =
    'Enter a partial description or code and select an option. The procedure code must be a CPT or HCPCS code with at least 5 characters.';

  const subLabel =
    customSubLabel || (showLinkAboveInput ? cptLinkSmall : undefined);

  return hasFeature('procedure-codes-list', features) ? (
    <AsyncMultiSelectField
      label="Procedure Code(s)"
      name="procedureCodesList"
      loadOptions={cptLookup}
      information={informationText}
      subLabel={subLabel}
      placeholder="Enter a partial description or code and select an option"
      noResultsMessage={(input) =>
        input?.trim()?.match(/^[a-zA-Z0-9]{5}$/)?.length ? (
          <div className="text-danger d-flex align-items-center justify-content-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              size="sm"
              className="me-1"
            />
            <small role="alert" className="fw-bold">
              The procedure code {input} does not require precertification
            </small>
          </div>
        ) : (
          <div role="alert">No results found</div>
        )
      }
      footer={showLinkAboveInput || hideFooter ? undefined : cptLinkBold}
      required
    />
  ) : (
    <AsyncSelectField
      required
      label="Procedure Code"
      footer={showLinkAboveInput || hideFooter ? undefined : cptLinkBold}
      information={informationText}
      subLabel={subLabel}
      name="procedureCode"
      loadOptions={cptLookup}
      placeholder="Enter a partial description or code and select an option"
      noResultsMessage={(input) =>
        input?.trim()?.match(/^[a-zA-Z0-9]{5}$/)?.length ? (
          <div className="text-danger d-flex align-items-center justify-content-center">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              size="sm"
              className="me-1"
            />
            <small role="alert" className="fw-bold">
              The procedure code {input} does not require precertification
            </small>
          </div>
        ) : (
          <div role="alert">No results found</div>
        )
      }
    />
  );
};

export const TypeOfCare: Record<string, string> = {
  Outpatient: 'Outpatient',
  Inpatient: 'Inpatient',
};

export function NewRequestStep() {
  const type: { label: string; value: string }[] = valuesCodeArray(TypeOfCare);
  const confirmationOptions: { label: string; value: boolean }[] = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
  ];
  return (
    <div className="pt-0">
      <h2>Veteran Referral Details</h2>
      <Row>
        <Col lg={6}>
          <InputField
            name="veteranReferralId"
            label="Veteran Referral ID Number"
            type="text"
            id="veteranReferralId"
            aria-label="Veteran Referral ID Number"
            required
            maxLength={12}
            information="The Veteran Referral ID Number is located on the VA Offline Referral Form and can also be found by contacting the referring VAMC/Provider"
          />
        </Col>
        <Col lg={6}>
          <InputField
            name="veteranIcn"
            label="Veteran ICN"
            type="text"
            id="veteranIcn"
            aria-label="Veteran ICN"
            required
            maxLength={17}
            information="The ICN is located on the VA Offline Referral Form and can also be found by contacting the referring VAMC/Provider."
          />
        </Col>
      </Row>
      <hr aria-hidden="true" />
      <h2>Precertification Details</h2>
      <Row className="justify-content-center">
        <Col lg={6}>
          <FormGroup>
            <PrecertCodeField />
          </FormGroup>
        </Col>

        <Col lg={6}>
          <FormGroup>
            <SelectField
              required
              options={type}
              label="Precertification Type"
              aria-label="Precertification Type"
              name="requestType"
            />
          </FormGroup>
        </Col>
      </Row>
      <Row className="justify-content-start">
        <Col lg={6}>
          <FormGroup>
            <div>
              <div className="mb-2">
                Are you requesting precertification for any of the episodic
                services listed in the description below?
              </div>
              <ul>
                <li className="text-start">Radiation therapy</li>
                <li className="text-start">Infusion/Chemo therapy</li>
                <li className="text-start">Physical/Occupational therapy</li>
                <li className="text-start">Cardiac or Pulmonary rehab</li>
                <li className="text-start">Dialysis</li>
              </ul>
              <SelectField
                required
                aria-label="Episodic Care - Refer to above list"
                options={confirmationOptions}
                label="Episodic Care - Refer to above list"
                name="episodicCare"
                information="For each specific episodic service, a precertification notification is only required for the first visit on that associated authorization."
              />
            </div>
            CPT copyright 2024 American Medical Association. All rights
            reserved.
          </FormGroup>
        </Col>
      </Row>
    </div>
  );
}

export const stepNewRequest: WizardStepProps = {
  title: 'New Precertification',
  schema: StepNewRequestSchema,
  element: <NewRequestStep />,
  path: 'new-request',
};
